import { BrowserModule } from '@angular/platform-browser'
import { NgModule } from '@angular/core'

import { HttpClientModule, HttpClient as Http } from '@angular/common/http'

import { FormsModule } from '@angular/forms'
import { LOCALE_ID } from '@angular/core'
import localeEs from '@angular/common/locales/es'
import localeEn from '@angular/common/locales/en'
import { registerLocaleData } from '@angular/common'
import { DecimalPipe } from '@angular/common'

import {
  TranslateModule,
  TranslateLoader,
  TranslatePipe,
} from '@ngx-translate/core'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'
import { CommonCompModule } from './common/common.module'
import { WidgetModule } from './widget/widget.module'

import { AuthModule } from '@auth0/auth0-angular'

import { AuthGuard } from './services/auth.guard'
import { AuthenticationService } from './services/auth.service'

import { AppComponent } from './app.component'
import { LoginComponent } from './section/login/login.component'

import { ContextService } from './services/context.service'
import { LocaleService } from './services/locale.service'

import { AppRoutingModule } from './app.routing.module'

import { LatitudeAltModule } from './latitude/latitude.module'
import { MobilityModule } from './mobility/mobility.module'
import { RouterModule } from '@angular/router'
import { environment } from '../environments/environment'
import { MobilityService } from './services/mobility.service'
import { IsolinesService } from './services/isolines.service'
import { UserModule } from './user/user.module'
import { UserService } from './services/user.service'
import { ComponentBuilderService } from './services/component-builder-service.service'
import {
  provideTanStackQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental'

export function getCurrentLocaleFactory(localeService: LocaleService) {
  if (localeService.locale === 'en') {
    registerLocaleData(localeEn, 'en')
  } else {
    registerLocaleData(localeEs, 'es')
  }
  return localeService.locale
}

export function createTranslateLoader(http: Http) {
  const template = environment.i18Template || 'default'
  return new TranslateHttpLoader(http, `./assets/i18n/${template}/`, '.json')
}

@NgModule({
  declarations: [AppComponent, LoginComponent],
  imports: [
    BrowserModule,
    HttpClientModule,
    RouterModule.forRoot([], { useHash: false }),
    AuthModule.forRoot({
      domain: (environment as any).AUTH_DOMAIN,
      clientId: (environment as any).AUTH_CLIENT_ID,
      authorizationParams: {
        audience: 'carto-cloud-native-api',
        redirect_uri: window.location.origin,
        scope: [
          'profile',
          'email',
          'read:current_user',
          'read:connections',
          'read:maps',
          'read:account',
        ].join(' '),
      },
      useRefreshTokens: true,
      cacheLocation: 'localstorage',
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [Http],
      },
    }),
    FormsModule,
    LatitudeAltModule,
    CommonCompModule,
    WidgetModule,
    AppRoutingModule,
    UserModule,
    MobilityModule,
  ],
  providers: [
    LocaleService,
    AuthGuard,
    AuthenticationService,
    ContextService,
    DecimalPipe,
    TranslatePipe,
    MobilityService,
    IsolinesService,
    UserService,
    ComponentBuilderService,
    {
      provide: LOCALE_ID,
      deps: [LocaleService],
      useFactory: getCurrentLocaleFactory,
    },
    provideTanStackQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 30 * 60 * 1000, // 30 minutes
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      }),
    ),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
